import type { FC, ReactNode } from 'react'
import type { GetStaticPaths, GetStaticProps } from 'next'

import Image from 'next/image'
import { ArrowLeft } from 'lucide-react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { Container } from '@/components/generals/container'
import { Eyebrow } from '@/components/generals/eyebrow'
import { ButtonLink } from '@/components/generals/button'
import { CtaBand } from '@/components/generals/cta-band'
import { SectionHeading } from '@/components/generals/section-heading'
import { TransitionLink } from '@/components/generals/transition-link'
import { Enter, HeroTitle } from '@/components/motion/hero-title'
import { Reveal } from '@/components/motion/reveal'
import { AnimatedHeading } from '@/components/motion/animated-heading'
import { ServiceBlock } from '@/components/landing/service/service-blocks'
import { ServiceCard } from '@/components/widgets/service-card'
import { ProjectCard } from '@/components/widgets/project-card'
import { CallbackForm } from '@/components/widgets/callback-form'
import { detailServices, getServiceBySlug, services } from '@/constants/services'
import { projects } from '@/constants/projects'
import { siteConfig } from '@/constants/site'

interface ServicePageProps {
    slug: string
}

const relatedProjectSlugs: Record<string, string[]> = {
    'broken-spring-repair': ['millbrook-spring-replacement'],
    'openers-smart-garage': ['pine-ridge-smart-opener'],
    'commercial-doors': ['stonebridge-workshop-doors'],
    'tune-up-maintenance': ['millbrook-spring-replacement']
}

const ServicePage: FC<ServicePageProps> = ({ slug }): ReactNode => {
    const service = getServiceBySlug(slug)

    if (!service?.detail) return null

    const { detail } = service
    const isEmergency = service.slug === 'emergency-repair'
    const related = projects.filter(project => relatedProjectSlugs[service.slug]?.includes(project.slug))
    const others = services.filter(item => item.slug !== service.slug).slice(0, 3)

    const renderCta = (cta: typeof detail.primaryCta, variant: 'primary' | 'outline') =>
        cta === 'call' ? (
            <ButtonLink href={siteConfig.phoneHref} variant={variant} size='lg' icon='phone'>
                Call {siteConfig.phone}
            </ButtonLink>
        ) : (
            <ButtonLink href={cta.href} variant={variant} size='lg'>
                {cta.label}
            </ButtonLink>
        )

    return (
        <Layout>
            <Seo title={service.title} description={detail.intro} />

            <section className='relative overflow-hidden pb-16 pt-36 md:pb-24 md:pt-44'>
                <div aria-hidden='true' className='pointer-events-none absolute -right-40 -top-40 size-[42rem] rounded-full bg-ember/[0.07] blur-3xl' />

                <Container className='relative'>
                    <Enter>
                        <TransitionLink
                            href='/services'
                            className='group mb-10 inline-flex items-center gap-2 text-sm font-medium text-slate transition-colors hover:text-ink'
                        >
                            <span className='flex size-8 items-center justify-center rounded-full border border-ink/12 transition-colors group-hover:border-ink/30'>
                                <ArrowLeft className='size-3.5 transition-transform duration-300 group-hover:-translate-x-0.5' aria-hidden='true' />
                            </span>
                            Back to services
                        </TransitionLink>
                    </Enter>

                    <div className='grid gap-12 lg:grid-cols-12 lg:items-end lg:gap-10'>
                        <div className='lg:col-span-7'>
                            <Enter delay={60}>
                                <Eyebrow>{detail.eyebrow}</Eyebrow>
                            </Enter>

                            <HeroTitle text={detail.title} className='mt-6 max-w-[16ch] text-h1 font-semibold text-ink' />

                            <Enter delay={420} className='mt-7 max-w-xl text-lead text-pretty text-slate'>
                                {detail.intro}
                            </Enter>

                            <Enter delay={520} className='mt-10'>
                                <dl className='grid grid-cols-3 overflow-hidden rounded-[22px] border border-ink/10 bg-card'>
                                    {detail.facts.map((fact, index) => (
                                        <div key={fact.label} className={index > 0 ? 'border-l border-ink/10 p-4 sm:p-5' : 'p-4 sm:p-5'}>
                                            <dt className='font-mono text-[10px] uppercase tracking-[0.16em] text-slate sm:text-[11px]'>{fact.label}</dt>
                                            <dd className='mt-2 font-heading text-[1.05rem] font-semibold leading-tight tracking-[-0.02em] text-ink sm:text-[1.35rem]'>{fact.value}</dd>
                                        </div>
                                    ))}
                                </dl>
                            </Enter>

                            <Enter delay={620} className='mt-8 flex flex-wrap gap-3'>
                                {renderCta(detail.primaryCta, 'primary')}
                                {renderCta(detail.secondaryCta, 'outline')}
                            </Enter>
                        </div>

                        <Enter delay={200} className='lg:col-span-5'>
                            <div className='relative aspect-[4/5] overflow-hidden rounded-[32px] bg-sand animate-door-reveal' style={{ animationDelay: 'calc(var(--enter-delay, 0ms) + 150ms)' }}>
                                <Image
                                    src={service.image}
                                    alt={service.imageAlt}
                                    fill
                                    priority
                                    sizes='(min-width: 1024px) 38vw, 100vw'
                                    className='object-cover animate-hero-zoom'
                                />
                            </div>
                        </Enter>
                    </div>
                </Container>
            </section>

            <section className='pb-10'>
                <Container>
                    {detail.sections.map((section, index) => (
                        <ServiceBlock key={section.title} section={section} index={index} />
                    ))}
                </Container>
            </section>

            {isEmergency && (
                <section id='callback' className='py-16 md:py-24'>
                    <Container>
                        <div className='rounded-[32px] border border-ink/10 bg-paper p-6 sm:p-10 md:p-14'>
                            <Eyebrow>Request a callback</Eyebrow>

                            <AnimatedHeading className='mt-5 text-h2 font-semibold text-ink'>
                                {detail.closing.title}
                            </AnimatedHeading>

                            <p className='mt-4 max-w-xl text-lead text-slate'>{detail.closing.body}</p>

                            <div className='mt-10'>
                                <CallbackForm />
                            </div>
                        </div>
                    </Container>
                </section>
            )}

            {related.length > 0 && (
                <section className='py-16 md:py-24'>
                    <Container>
                        <SectionHeading eyebrow="From our job log" title="The same kind of job at a real home, before and after." layout='stack' />

                        <Reveal selector=':scope > *' className='mt-12 grid gap-8 md:grid-cols-2'>
                            {related.map(project => (
                                <ProjectCard key={project.slug} project={project} />
                            ))}
                        </Reveal>
                    </Container>
                </section>
            )}

            <section className='py-16 md:py-24'>
                <Container>
                    <SectionHeading
                        eyebrow="Other services"
                        title="More of what we fix and install."
                        action={<ButtonLink href='/services' variant='outline'>See every service</ButtonLink>}
                    />

                    <Reveal selector=':scope > *' className='mt-12 grid gap-4 md:grid-cols-3'>
                        {others.map(item => (
                            <ServiceCard key={item.slug} service={item} className='min-h-[26rem]' sizes='(min-width: 768px) 33vw, 100vw' />
                        ))}
                    </Reveal>
                </Container>
            </section>

            {!isEmergency && (
                <CtaBand
                    eyebrow={service.title}
                    title={detail.closing.title}
                    body={detail.closing.body}
                    primary={detail.closing.cta}
                />
            )}
        </Layout>
    )
}

export const getStaticPaths: GetStaticPaths = async () => ({
    paths: detailServices.map(service => ({ params: { slug: service.slug } })),
    fallback: false
})

export const getStaticProps: GetStaticProps<ServicePageProps> = async ({ params }) => {
    const slug = typeof params?.slug === 'string' ? params.slug : ''

    if (!getServiceBySlug(slug)) return { notFound: true }

    return { props: { slug } }
}

export default ServicePage
